import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Zap } from 'lucide-react';
import { smoothScrollToElement } from '../lib/smoothScroll';

const LOGO_URL = "/media/logo.png";
const HERO_VIDEO_URL = "/media/intro_hpc.mp4";

const HeroSection = () => {
  const scrollToGallery = () => {
    smoothScrollToElement('#gallery');
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Background Video */}
      <div className="absolute inset-0 overflow-hidden">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src={HERO_VIDEO_URL}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/20 to-black/80" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(220,38,38,0.16),transparent_55%)]" />
      </div>

      {/* Lightning Effect Overlays */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-red-500/30 to-transparent"
          animate={{ opacity: [0, 1, 0], scaleY: [0.5, 1, 0.5] }}
          transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
        />
        <motion.div
          className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-white/20 to-transparent"
          animate={{ opacity: [0, 0.8, 0], scaleY: [0.3, 1, 0.3] }}
          transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 4 }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="mb-8"
        >
          <img
            src={LOGO_URL}
            alt="Heartland Plains Chasing"
            className="mx-auto h-40 md:h-56 lg:h-64 w-auto drop-shadow-2xl"
          />
        </motion.div>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="text-xl md:text-2xl lg:text-3xl text-gray-300 mb-4 font-light tracking-wide"
        >
          A Kansas-based storm chasing team established in 2026 with members across the Plains and Midwest.
        </motion.p>

        {/* Established Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="flex items-center justify-center gap-6 mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/40 rounded-full text-red-400 text-sm font-medium">
            <Zap size={16} />
            Storm Chasers
          </span>
        </motion.div>

        {/* CTA Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          onClick={scrollToGallery}
          className="group relative px-8 py-4 bg-red-600 text-white font-bold text-lg rounded-full overflow-hidden transition-all duration-300 hover:bg-red-500 hover:shadow-lg hover:shadow-red-500/30"
        >
          <span className="relative z-10">Explore Our Gallery</span>
          <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
        </motion.button>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <button onClick={scrollToGallery} className="text-white/60 hover:text-white transition-colors">
          <ChevronDown size={32} />
        </button>
      </motion.div>
    </section>
  );
};

export default HeroSection;
